import React, { useState } from "react";
import "./AddPatientModal.css";
import axios from "axios";
import { toast } from "react-toastify";
import CustomDatePicker from "../resuableComponents/CustomDatePicker";
import CustomDropdown from "../resuableComponents/CustomDropdown";
import dayjs from "dayjs";
import { FaTimes } from "react-icons/fa";

const AddPatientModal = ({
  isOpen,
  onClose,
  onPatientAdded,
  fetchPatients,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    dob: "",
    gender: "",
    contact: "",
    age: "",
    email: "",
    address: "",
    referenceType: "",
    campaign_name: "",
    socialPlatform: "",
  });

  const [loading, setLoading] = useState(false);

  const handleDateChange = (date) => {
    let updatedFormData = { ...formData };

    if (date && dayjs.isDayjs(date)) {
      // Format date to YYYY-MM-DD for the form data
      const formattedDate = date.format("YYYY-MM-DD");
      updatedFormData.dob = formattedDate;

      // Calculate age from dob
      const today = dayjs();
      const age = today.diff(date, "year");
      updatedFormData.age = age >= 0 ? age.toString() : "";
    } else {
      updatedFormData.dob = "";
      updatedFormData.age = "";
    }

    setFormData(updatedFormData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "radio") {
      setFormData({ ...formData, [name]: value === "true" });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const socialPlatforms = [
    { value: "google", label: "Google" },
    { value: "facebook", label: "Facebook" },
    { value: "instagram", label: "Instagram" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "twitter", label: "Twitter" },
    { value: "youtube", label: "YouTube" },
  ];

  const handleReferenceType = (value) => {
    setFormData((prev) => {
      const isCamp = value === "internal_camp" || value === "external_camp";
      return {
        ...prev,
        referenceType: value,
        campaign_name: isCamp ? prev.campaign_name : "",
        socialPlatform: value === "social_media" ? prev.socialPlatform : "",
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isCamp =
      formData.referenceType === "internal_camp" ||
      formData.referenceType === "external_camp";
    const isSocial = formData.referenceType === "social_media";

    if (isCamp && !formData.campaign_name.trim()) {
      toast.error("Please enter the camp name.");
      return;
    }
    if (isSocial && !formData.socialPlatform) {
      toast.error("Please select a social media platform.");
      return;
    }

    setLoading(true);
    try {
      const authData = JSON.parse(localStorage.getItem("authData"));

      if (!authData?.accessToken) {
        throw new Error("Authentication required. Please login again.");
      }

      const branch = authData?.user?.branch;
      const branchName = branch?.name || branch;
      if (!branchName) {
        toast.error("Your account has no branch assigned. Cannot register a patient.");
        setLoading(false);
        return;
      }

      // Phone number validation (optional, but must be 10 digits if provided)
      if (formData.contact) {
        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(formData.contact)) {
          toast.error("Please enter a valid 10-digit contact number.");
          setLoading(false);
          return;
        }
      }

      const payload = {
        name: formData.name,
        dob: formData.dob,
        gender: formData.gender,
        contact: formData.contact,
        age: formData.age,
        email: formData.email,
        address: formData.address,
        branch: branchName,
        ...(formData.referenceType
          ? {
              referenceType: formData.referenceType,
              campaign_name: isCamp ? formData.campaign_name.trim() : "",
              socialPlatform: isSocial ? formData.socialPlatform : undefined,
            }
          : {}),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}register-patient`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
          },
        }
      );

      toast.success("Patient registered successfully!");

      setTimeout(() => {
        // Prefer the full patient object from API if available
        const addedPatient = response.data?.patient || {
          ...formData,
          _id: Date.now().toString(), // fallback id if not returned
        };

        onPatientAdded(addedPatient);
        onClose();
        fetchPatients(); // Refresh the patient list

        // Reset form after submission
        setFormData({
          name: "",
          dob: "",
          gender: "",
          contact: "",
          age: "",
          email: "",
          address: "",
          referenceType: "",
          campaign_name: "",
          socialPlatform: "",
        });
      }, 100);
    } catch (error) {
      console.error("Registration error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to register patient. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="patient-modal-overlay">
      <div className="patient-modal-content">
        <div className="patient-modal-header">
          <h3>Add New Patient</h3>
          <button
            type="button"
            className="patient-close-btn"
            onClick={onClose}
            disabled={loading}
          >
            <FaTimes />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="patient-modal-form">
          <div className="patient-modal-body">
            <div className="patient-form-group">
              <label className="patient-form-label" htmlFor="name">
                Name
              </label>
              <input
                id="name"
                name="name"
                placeholder="Full Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="patient-form-group">
              <label className="patient-form-label">Date of Birth</label>
              <CustomDatePicker
                // label="Select date of birth"
                value={formData.dob ? dayjs(formData.dob) : null}
                onChange={handleDateChange}
                maxDate={dayjs()}
                placeholder="Select date of birth"
                required
              />
            </div>
            <div className="patient-form-group">
              <label className="patient-form-label">Gender</label>
              <CustomDropdown
                label="Select Gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                options={[
                  { value: "male", label: "Male" },
                  { value: "female", label: "Female" },
                  { value: "other", label: "Other" },
                ]}
              // required
              />
            </div>
            <div className="patient-form-group">
              <label className="patient-form-label" htmlFor="contact">
                Contact Number
              </label>
              <input
                id="contact"
                name="contact"
                placeholder="Contact Number"
                value={formData.contact}
                onChange={handleChange}
              />
            </div>
            <div className="patient-form-group">
              <label className="patient-form-label" htmlFor="age">
                Age
              </label>
              <input
                id="age"
                name="age"
                type="number"
                min="0"
                placeholder="Age"
                value={formData.age}
                onChange={handleChange}
                required
              />
            </div>
            <div className="patient-form-group">
              <label className="patient-form-label" htmlFor="email">
                Email (optional)
              </label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="Email (optional)"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            <div className="patient-form-group">
              <label className="patient-form-label" htmlFor="address">
                Address (optional)
              </label>
              <input
                id="address"
                name="address"
                placeholder="Address (optional)"
                value={formData.address}
                onChange={handleChange}
              />
            </div>
            <div className="patient-form-group">
              <label className="patient-form-label">Reference (optional)</label>
              <div className="patient-radio-group">
                {[
                  { value: "internal_camp", label: "Internal Camp" },
                  { value: "external_camp", label: "External Camp" },
                  { value: "social_media", label: "Social Media" },
                ].map((option) => (
                  <label
                    className={`patient-radio-label${
                      formData.referenceType === option.value ? " selected" : ""
                    }`}
                    key={option.value}
                  >
                    <input
                      type="radio"
                      name="referenceType"
                      value={option.value}
                      checked={formData.referenceType === option.value}
                      onChange={() => handleReferenceType(option.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
            {(formData.referenceType === "internal_camp" ||
              formData.referenceType === "external_camp") && (
              <div className="patient-form-group">
                <label className="patient-form-label" htmlFor="campaign_name">
                  Camp name
                </label>
                <input
                  id="campaign_name"
                  name="campaign_name"
                  placeholder="Name of the camp"
                  value={formData.campaign_name}
                  onChange={handleChange}
                />
              </div>
            )}
            {formData.referenceType === "social_media" && (
              <div className="patient-form-group">
                <label className="patient-form-label">Platform</label>
                <CustomDropdown
                  label="Select platform"
                  name="socialPlatform"
                  value={formData.socialPlatform}
                  onChange={handleChange}
                  options={socialPlatforms}
                />
              </div>
            )}
          </div>
          <div className="patient-modal-footer">
            <button
              type="button"
              className="patient-cancel-btn"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="patient-submit-btn" disabled={loading}>
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Registering...
                </>
              ) : (
                "Register Patient"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPatientModal;
